import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useForm, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import RadioGroupField from 'components/RadioGroupField';
import { Button } from 'ncoded-component-library';
import validations from 'validations';
import Companies from '../Companies';
import { scrollToErrorsOnSubmit } from 'utils';
import MemberOf from '../MemberOf';
import RecipientDetails from '../RecipientDetails';
import EmailField from 'components/EmailField';
import ContactNumberField from 'components/ContactNumberField';

import './AccountantForm.styles.scss';
import './AccountantForm.styles.responsive.scss';

type AccountantFormProps = {
  className?: string;
};

const AccountantForm: React.FC<AccountantFormProps> = (props) => {
  const { className } = props;

  const { t } = useTranslation();
  const {
    values: { clientDetails },
  } = useFormState();

  const { submit } = useForm();
  const { invalid, submitting, initialValues } = useFormState();

  const classes = classNames('accountant-form', className);

  return (
    <div className={classes}>
      <p className="bold-label margin">{t('clientsDetails')}</p>
      <div className="accountant-form__container">
        <Field
          name="clientDetails.firstName"
          component={InputField}
          label={t('firstName')}
          required
          validate={validations.required()}
        />
        <Field
          name="clientDetails.lastName"
          component={InputField}
          label={t('lastName')}
          required
          validate={validations.required()}
        />
        <EmailField name="clientDetails.email" required />
        <ContactNumberField name="clientDetails.mobilePhoneNumber" required />
        <Field
          name="clientDetails.location"
          component={InputField}
          label={t('State')}
          required
          validate={validations.required()}
        />
        {initialValues?.clientDetails?.companyEmployer && (
          <Field
            name="clientDetails.companyEmployer"
            component={InputField}
            label={t('companyOrEmployer')}
          />
        )}
      </div>

      <p className="bold-label margin">{t('certifyThatThePerson')}</p>
      <Field
        name="clientDetails.isMyClient"
        label={t('isMyClient')}
        type="checkbox"
        className="confirm-client"
        component={CheckboxField}
        validate={validations.checkedFieldValidation()}
      />

      <p className="bold-label">{t('andIsEither')}</p>
      <RadioGroupField
        name="clientDetails.isWholeSaleInvestor"
        className="radio"
        options={[
          {
            label: <p>{t('aWholesaleInvestor')}</p>,
            value: 'yes',
          },
          {
            label: (
              <p>
                <span className="not">{t('not')}</span>{' '}
                {t('aWholesaleInvestor')}
              </p>
            ),
            value: 'no',
          },
        ]}
      />

      <p className="bold-label">{t('andHas')}</p>
      <CheckboxFieldGroup
        name="incomeAndGross"
        required
        options={[
          {
            label: t('incomeAndGross1'),
            value: 'hasNetAssets',
          },
          {
            label: t('incomeAndGross2'),
            value: 'hasGrossIncome',
          },
        ]}
      />
      <p className="checkbox-descirption">
        {t('youMustSelectAtLeastOneOptionOfTwo')}
      </p>
      <hr />

      <p className="bold-label">
        {t('isAustralianCompanyDirector', {
          firstName: clientDetails?.firstName || '{{ First Name }}',
          lastName: clientDetails?.lastName || '{{ Last Name }}',
        })}
      </p>
      <RadioGroupField
        name="clientDetails.isCompanyDirector"
        className="radio"
        options={[
          {
            label: t('yes'),
            value: 'yes',
          },
          {
            label: t('no'),
            value: 'no',
          },
        ]}
      />
      {clientDetails?.isCompanyDirector === 'yes' && <Companies />}
      <hr />

      <p className="accountant-form__title">{t('myDetails')}</p>
      <Field
        name="accountantDetails.isQualifiedAccountant"
        label={t('certifyThatImAccountant')}
        type="checkbox"
        component={CheckboxField}
        validate={validations.checkedFieldValidation()}
      />
      <div className="accountant-form__container">
        <Field
          name="accountantDetails.firstName"
          component={InputField}
          label={t('firstName')}
          validate={validations.required()}
        />
        <Field
          name="accountantDetails.lastName"
          component={InputField}
          label={t('lastName')}
          validate={validations.required()}
        />
        <Field
          className="accountant-form__container__firm"
          name="accountantDetails.firm"
          component={InputField}
          label={t('firm')}
          validate={validations.required()}
        />
      </div>

      <p className="accountant-form__title">{t('imMemberOf')}</p>
      <MemberOf />
      <hr />

      <p className="bold-label">{t('whatIsResolveInvestorForm')}</p>
      <Field
        name="createAccountOnResolve"
        label={t('createAccountOnResolve')}
        type="checkbox"
        component={CheckboxField}
      />

      <p className="bold-label">{t('recipientDetailsTitle')}</p>
      <RecipientDetails />

      <Button
        type="submit"
        disabled={submitting}
        onClick={() => {
          scrollToErrorsOnSubmit(invalid, submit);
        }}
      >
        {t('submit')}
      </Button>
    </div>
  );
};

export default AccountantForm;
